/**
 * ControlsPanel — the shared transport-controls block used by every editor:
 * a configurable list of value steppers (BPM / Offset / Speed / …) followed by
 * the Auto BPM / Auto Offset buttons.
 *
 * Presentational + config-driven: each editor passes the steppers it has (label,
 * value, handlers) and its own auto-detect handlers. The look (shared StepperInput
 * + Flow-style Auto buttons in a snug pill) is identical everywhere.
 */

import React from 'react';
import StepperInput from './StepperInput';
import { autoRowStyle, autoBtnStyle, editorBtnState } from './editorSharedStyles';

export interface StepperDef {
  label: string;
  value: string | number;
  onDec: () => void;
  onInc: () => void;
  /** On-change editable value field. */
  editable?: boolean;
  onValueChange?: (raw: string) => void;
  /** Commit-on-blur editable value field (preferred — used by Flow's steppers). */
  onCommitValue?: (raw: string) => void;
  editTitle?: string;
  /** No decrement / increment arrows: the value is typed. */
  hideArrows?: boolean;
}

export interface ControlsPanelProps {
  steppers: StepperDef[];
  onAutoBpm?: () => void;
  autoBpmBusy?: boolean;
  onAutoOffset?: () => void;
  autoOffsetBusy?: boolean;
  /** Greys out + disables both Auto buttons (e.g. no audio loaded). */
  autoDisabled?: boolean;
}

function AutoBtn({ label, icon, busy, disabled, onClick }: {
  label: string; icon: string; busy?: boolean; disabled?: boolean; onClick: () => void;
}) {
  const off = disabled || busy;
  return (
    <button
      onClick={onClick}
      disabled={off}
      title={label}
      style={{ ...autoBtnStyle, ...editorBtnState(!!busy), opacity: disabled ? 0.4 : 1, cursor: off ? 'default' : 'pointer' }}
    >
      <span className="ic-themed" style={{ fontSize: 12, lineHeight: 1, color: 'var(--accent-soft)' }}>{busy ? '…' : icon}</span>
      <span>{label}</span>
    </button>
  );
}

export default function ControlsPanel({
  steppers, onAutoBpm, autoBpmBusy, onAutoOffset, autoOffsetBusy, autoDisabled,
}: ControlsPanelProps) {
  return (
    <>
      {steppers.map(s => (
        <StepperInput
          key={s.label}
          label={s.label}
          value={String(s.value)}
          onDecrement={s.onDec}
          onIncrement={s.onInc}
          editable={s.editable}
          onValueChange={s.onValueChange}
          onCommitValue={s.onCommitValue}
          editTitle={s.editTitle}
          hideArrows={s.hideArrows}
          hoverAccent
        />
      ))}
      {(onAutoBpm || onAutoOffset) && (
        <div style={autoWrapStyle}>
          <div style={autoRowStyle}>
            {onAutoBpm && <AutoBtn label="Auto BPM" icon="♪" busy={autoBpmBusy} disabled={autoDisabled} onClick={onAutoBpm} />}
            {onAutoOffset && <AutoBtn label="Auto Offset" icon="⇥" busy={autoOffsetBusy} disabled={autoDisabled} onClick={onAutoOffset} />}
          </div>
        </div>
      )}
    </>
  );
}

// Snug pill that wraps the Auto buttons (matches the Flow / VR look).
const autoWrapStyle: React.CSSProperties = {
  marginTop: 4,
  padding: '5px 6px',
  borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
};
